import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const FilterSidebar = ({ isOpen, onClose, onApplyFilters, activeFilters = {} }) => {
  const [priceRange, setPriceRange] = useState([activeFilters?.priceMin || 0, activeFilters?.priceMax || 5000]);
  const [selectedDuration, setSelectedDuration] = useState(activeFilters?.duration || '');
  const [selectedCategories, setSelectedCategories] = useState(activeFilters?.categories || []);
  const [selectedRating, setSelectedRating] = useState(activeFilters?.rating || '');
  const [selectedDifficulty, setSelectedDifficulty] = useState(activeFilters?.difficulty || '');
  const [quickFilters, setQuickFilters] = useState(activeFilters?.quickFilters || []);

  const durationOptions = [
    { value: '', label: 'Any Duration' },
    { value: '1-3', label: '1-3 hours' },
    { value: '4-8', label: '4-8 hours' },
    { value: 'full-day', label: 'Full Day' },
    { value: '2-3-days', label: '2-3 Days' },
    { value: '1-week', label: '1 Week+' }
  ];

  const categoryOptions = [
    'Adventure & Outdoor',
    'Cultural & Historical',
    'Food & Drink',
    'Nature & Wildlife',
    'City Tours',
    'Beach & Water Sports',
    'Art & Museums',
    'Photography Tours',
    'Family Friendly',
    'Romantic Getaways'
  ];

  const ratingOptions = [
    { value: '', label: 'Any Rating' },
    { value: '4.5', label: '4.5+ Stars' },
    { value: '4.0', label: '4.0+ Stars' },
    { value: '3.5', label: '3.5+ Stars' },
    { value: '3.0', label: '3.0+ Stars' }
  ];

  const difficultyOptions = [
    { value: '', label: 'Any Difficulty' },
    { value: 'easy', label: 'Easy' },
    { value: 'moderate', label: 'Moderate' },
    { value: 'challenging', label: 'Challenging' },
    { value: 'extreme', label: 'Extreme' }
  ];

  const quickFilterOptions = [
    'Free Cancellation',
    'Instant Booking',
    'Small Groups',
    'Skip the Line',
    'Private Tour',
    'Audio Guide',
    'Wheelchair Accessible',
    'Pet Friendly'
  ];

  const handleCategoryChange = (category, checked) => {
    if (checked) {
      setSelectedCategories([...selectedCategories, category]);
    } else {
      setSelectedCategories(selectedCategories?.filter(c => c !== category));
    }
  };

  const handleQuickFilterChange = (filter, checked) => {
    if (checked) {
      setQuickFilters([...quickFilters, filter]);
    } else {
      setQuickFilters(quickFilters?.filter(f => f !== filter));
    }
  };

  const handleClearFilters = () => {
    setPriceRange([0, 5000]);
    setSelectedDuration('');
    setSelectedCategories([]);
    setSelectedRating('');
    setSelectedDifficulty('');
    setQuickFilters([]);
  };

  const handleApplyFilters = () => {
    const filters = {
      priceMin: priceRange?.[0],
      priceMax: priceRange?.[1],
      duration: selectedDuration,
      categories: selectedCategories,
      rating: selectedRating,
      difficulty: selectedDifficulty,
      quickFilters: quickFilters
    };
    
    onApplyFilters(filters);
    onClose();
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (priceRange?.[0] > 0 || priceRange?.[1] < 5000) count++;
    if (selectedDuration) count++;
    if (selectedCategories?.length > 0) count++;
    if (selectedRating) count++;
    if (selectedDifficulty) count++;
    if (quickFilters?.length > 0) count++;
    return count;
  };

  return (
    <>
      {/* Mobile Backdrop */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}
      {/* Sidebar */}
      <aside 
        className={`fixed top-20 left-0 h-[calc(100vh-5rem)] w-80 bg-white border-r border-border z-50 transform transition-transform duration-300 ease-out overflow-y-auto ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0`}
      >
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-2">
              <h2 className="text-lg font-semibold text-foreground">Filters</h2>
              {getActiveFilterCount() > 0 && (
                <span className="bg-primary text-white text-xs px-2 py-1 rounded-full">
                  {getActiveFilterCount()}
                </span>
              )}
            </div>
            <div className="flex items-center space-x-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClearFilters}
                className="text-muted-foreground hover:text-foreground"
              >
                Clear All
              </Button>
              <button
                onClick={onClose}
                className="lg:hidden p-1 rounded-md hover:bg-muted transition-colors"
              >
                <Icon name="X" size={18} />
              </button>
            </div>
          </div>

          {/* Price Range */}
          <div className="mb-6">
            <h3 className="font-medium text-foreground mb-3">Price Range</h3>
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <Input
                  type="number"
                  placeholder="Min"
                  value={priceRange?.[0]}
                  onChange={(e) => setPriceRange([parseInt(e?.target?.value) || 0, priceRange?.[1]])}
                  className="flex-1"
                />
                <span className="text-muted-foreground">-</span>
                <Input
                  type="number"
                  placeholder="Max"
                  value={priceRange?.[1]}
                  onChange={(e) => setPriceRange([priceRange?.[0], parseInt(e?.target?.value) || 5000])}
                  className="flex-1"
                />
              </div>
              <div className="text-sm text-muted-foreground">
                ${priceRange?.[0]} - ${priceRange?.[1]}
              </div>
            </div>
          </div>

          {/* Duration */}
          <div className="mb-6">
            <h3 className="font-medium text-foreground mb-3">Duration</h3>
            <Select
              options={durationOptions}
              value={selectedDuration}
              onChange={setSelectedDuration}
              placeholder="Select duration"
            />
          </div>

          {/* Categories */}
          <div className="mb-6">
            <h3 className="font-medium text-foreground mb-3">Categories</h3>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {categoryOptions?.map((category) => (
                <Checkbox
                  key={category}
                  label={category}
                  checked={selectedCategories?.includes(category)}
                  onChange={(e) => handleCategoryChange(category, e?.target?.checked)}
                />
              ))}
            </div>
          </div>

          {/* Rating */}
          <div className="mb-6">
            <h3 className="font-medium text-foreground mb-3">Minimum Rating</h3>
            <Select
              options={ratingOptions}
              value={selectedRating}
              onChange={setSelectedRating}
              placeholder="Any rating"
            />
          </div>

          {/* Difficulty Level */}
          <div className="mb-6">
            <h3 className="font-medium text-foreground mb-3">Difficulty Level</h3>
            <Select
              options={difficultyOptions}
              value={selectedDifficulty}
              onChange={setSelectedDifficulty}
              placeholder="Any difficulty"
            />
          </div>

          {/* Quick Filters */}
          <div className="mb-6">
            <h3 className="font-medium text-foreground mb-3">Quick Filters</h3>
            <div className="space-y-2">
              {quickFilterOptions?.map((filter) => (
                <Checkbox
                  key={filter}
                  label={filter}
                  checked={quickFilters?.includes(filter)}
                  onChange={(e) => handleQuickFilterChange(filter, e?.target?.checked)}
                />
              ))}
            </div>
          </div>

          {/* Apply Filters Button */}
          <div className="sticky bottom-0 bg-white pt-4 border-t border-border">
            <Button
              onClick={handleApplyFilters}
              className="w-full"
              iconName="Filter"
              iconPosition="left"
            >
              Apply Filters ({getActiveFilterCount()})
            </Button>
          </div>
        </div>
      </aside>
    </>
  );
};

export default FilterSidebar;